import React, { useState, useCallback, useRef } from "react";
import { Box, Typography, Paper, Stack, CircularProgress } from "@mui/material";
import { LocationOn } from "@mui/icons-material";
import { Dialog } from "../../../components/Dialogs/Dialog";
import { CustomFormLabel } from "../../../components/formsComponents";
import GooglePlacesAutocomplete, {
  geocodeByPlaceId,
} from "react-google-places-autocomplete";
//@ts-ignore
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";

interface IMapPickerProps {
  open: boolean;
  onClose: () => void;
  onHandleLocationSelect: (place: any) => void;
}

interface ISelectedLocation {
  latitude: number;
  longitude: number;
  name: string;
  address: string;
}

const containerStyle = {
  width: "100%",
  height: "400px",
};

const libraries: ("places" | "geometry")[] = ["places"];

export const MapPicker = (props: IMapPickerProps) => {
  const { open, onClose, onHandleLocationSelect } = props;
  const [selectedLocation, setSelectedLocation] =
    useState<ISelectedLocation | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 18.5204, lng: 73.8567 }); // Default to Pune
  const [mapZoom, setMapZoom] = useState(12);
  const [marker, setMarker] = useState<{ lat: number; lng: number } | null>(
    null
  );
  const mapRef = useRef<google.maps.Map | null>(null);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey:
      process.env.REACT_APP_GOOGLE_MAP_API_KEY ||
      process.env.REACT_APP_GOOGLE_MAP_KEY ||
      "",
    libraries,
  });

  const handlePlaceChange = async (place: any) => {
    // Handle case when user clears the selection
    if (!place || !place.value) {
      setSelectedLocation(null);
      setSelectedPlace(null);
      return;
    }

    const placeId = place.value.place_id;
    if (!placeId) return;

    setIsLoading(true);
    setSelectedPlace(place);

    try {
      // Get the detailed place information using the placeId (same as code 2)
      const response = await geocodeByPlaceId(placeId);
      const { geometry, address_components, formatted_address } = response[0];
      const { lat, lng } = geometry.location;

      // Extract address components
      let address = formatted_address || "";
      let name = "";

      // Try to get a meaningful name (establishment name, locality, or route)
      for (const component of address_components) {
        const types = component.types;
        if (types.includes("establishment")) {
          name = component.long_name;
          break;
        } else if (types.includes("locality") && !name) {
          name = component.long_name;
        } else if (types.includes("route") && !name) {
          name = component.long_name;
        }
      }

      // If no name found, use the first part of the address
      if (!name && address) {
        name = address.split(",")[0];
      }

      const locationData = {
        latitude: lat(),
        longitude: lng(),
        name: name || "Selected Location",
        address: address,
      };

      setSelectedLocation(locationData);
      setMarker({ lat: lat(), lng: lng() });
      setMapCenter({ lat: lat(), lng: lng() });
      setMapZoom(15);

      // Center map on selected location
      if (mapRef.current) {
        mapRef.current.setCenter({ lat: lat(), lng: lng() });
        mapRef.current.setZoom(15);
      }

      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching location data:", error);
      setIsLoading(false);
      setSelectedLocation(null);
      setSelectedPlace(null);
    }
  };

  const handleMapClick = async (e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;

    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    setMarker({ lat, lng });
    setIsLoading(true);

    try {
      // Reverse Geocode to get address
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(
        { location: { lat, lng } },
        (results: google.maps.GeocoderResult[] | null, status) => {
          setIsLoading(false);
          if (status === "OK" && results && results[0]) {
            const address = results[0].formatted_address;
            let name = "Selected Location";

            // Try to get a meaningful name from address components
            const addressComponents = results[0].address_components;
            if (addressComponents) {
              for (const component of addressComponents) {
                const types = component.types;
                if (types.includes("establishment")) {
                  name = component.long_name;
                  break;
                } else if (
                  types.includes("locality") &&
                  name === "Selected Location"
                ) {
                  name = component.long_name;
                } else if (
                  types.includes("route") &&
                  name === "Selected Location"
                ) {
                  name = component.long_name;
                }
              }
            }

            // If no name found, use first part of address
            if (name === "Selected Location" && address) {
              name = address.split(",")[0];
            }

            const locationData = {
              latitude: lat,
              longitude: lng,
              address: address,
              name: name,
            };

            setSelectedLocation(locationData);

            // Create a place object for map clicks using the place_id from geocoding result
            // This allows handleLocationSelect to work properly with geocodeByPlaceId
            const placeFromMap = {
              value: {
                place_id: results[0].place_id,
                description: address,
              },
              label: name,
            };
            setSelectedPlace(placeFromMap);
          } else {
            // If geocoding fails, still set the location with coordinates
            const locationData = {
              latitude: lat,
              longitude: lng,
              address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
              name: "Selected Location",
            };
            setSelectedLocation(locationData);

            // If geocoding fails, we need to create coordinates-based place object
            // We'll use the coordinates directly and handleLocationSelect will need to handle this
            // For now, we'll create a minimal place object - but this might need special handling
            const placeFromMap = {
              value: {
                place_id: null, // No place_id available
                description: locationData.address,
                geometry: {
                  location: {
                    lat: () => lat,
                    lng: () => lng,
                  },
                },
                formatted_address: locationData.address,
                address_components: [],
              },
              label: "Selected Location",
            };
            setSelectedPlace(placeFromMap);
          }
        }
      );
    } catch (error) {
      console.error("Error reverse geocoding:", error);
      setIsLoading(false);
    }
  };

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const handleSendLocation = () => {
    if (selectedPlace) {
      onHandleLocationSelect(selectedPlace);
      // Reset state after sending
      setSelectedLocation(null);
      setSelectedPlace(null);
    }
  };

  const handleClose = () => {
    // Reset state on close
    setSelectedLocation(null);
    setSelectedPlace(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      title="Select Location"
      onClose={handleClose}
      size="lg"
      actions={[
        {
          type: "button",
          label: "Cancel",
          variant: "text",
          onClick: handleClose,
        },
        {
          type: "button",
          label: "Send Location",
          variant: "contained",
          onClick: handleSendLocation,
          disabled: !selectedLocation,
        },
      ]}
    >
      <Box>
        {isLoaded ? (
          <GooglePlacesAutocomplete
            apiKey={process.env.REACT_APP_GOOGLE_MAP_API_KEY}
            selectProps={{
              placeholder: "Search for a location...",
              isClearable: true,
              onChange: handlePlaceChange,
            }}
            autocompletionRequest={{
              componentRestrictions: {
                country: ["in"],
              },
            }}
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
            }}
          >
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading...
            </Typography>
          </Box>
        )}

        {isLoading && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mt: 2,
              p: 2,
            }}
          >
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading location details...
            </Typography>
          </Box>
        )}

        {selectedLocation && !isLoading && (
          <Paper
            elevation={2}
            sx={{
              mt: 3,
              p: 2,
              bgcolor: "background.paper",
              borderRadius: 2,
            }}
          >
            <Stack spacing={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <LocationOn color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  {selectedLocation.name}
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary">
                {selectedLocation.address}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  flexWrap: "wrap",
                  pt: 1,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Latitude
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedLocation.latitude.toFixed(6)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Longitude
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedLocation.longitude.toFixed(6)}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Paper>
        )}

        {!selectedLocation && !isLoading && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 2, fontSize: "0.75rem" }}
          >
            Search or click on the map to select a location
          </Typography>
        )}

        {/* Google Map */}
        <Box
          sx={{
            mt: 3,
            position: "relative",
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          {!isLoaded ? (
            <Box
              sx={{
                textAlign: "center",
                p: 3,
                height: containerStyle.height,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Loading map...
              </Typography>
            </Box>
          ) : (
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={mapCenter}
              zoom={mapZoom}
              onClick={handleMapClick}
              onLoad={onMapLoad}
              options={{
                disableDefaultUI: false,
                zoomControl: true,
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: true,
              }}
            >
              {marker && <Marker position={marker} />}
            </GoogleMap>
          )}

          {isLoading && (
            <Box
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                bgcolor: "background.paper",
                p: 1,
                borderRadius: 1,
                boxShadow: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CircularProgress size={20} />
              <Typography variant="caption">Loading...</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};
